"use client"

import { useClientTranslation } from "@/modules/App/components/i18n/i18n.client"
import { useMounted } from "@/shared/hooks/use-mounted"
import { getStepNumber, ROUTES, ROUTE_PREFIXES } from "@/shared/routing/routes"
import { isMultiPet } from "@/shared/routing/route-resolver"
import { shouldDisplayBanner } from "@/shared/utils/banner"
import { SEND_MY_QUOTE_ENABLED } from "@/shared/utils/config"
import { isSendMyQuoteEligible } from "@/shared/utils/is-send-my-quote-eligible"
import { newSubscriptionFlowService } from "@/store/machine"
import { Banner, Skeleton, cn } from "@dalmatech/ui"
import { useActor } from "@xstate/react"
import { usePathname } from "next/navigation"
import * as R from "ramda"
import { useEffect, useState } from "react"
import { Trans } from "react-i18next"

import { Breadcrumb } from "../Breadcrumb/breadcrumb"
import { SendMyQuote } from "../SendMyQuote/send-my-quote"
import { SendMyQuoteV2 } from "../SendMyQuote/send-my-quote-v2"
import { useSmqVariant } from "../SendMyQuote/use-smq-variant"
import { BackButton } from "./back-button"
import { DalmaLogoLink } from "./buttons"
import * as styles from "./navbar.styled"
import SelectLanguage from "./select-language"

export const Navbar = () => {
  const isMounted = useMounted()
  const { t } = useClientTranslation("global")
  const [state] = useActor(newSubscriptionFlowService)
  const pathname = usePathname()
  const [promoCode, setPromoCode] = useState<string | null | undefined>(null)
  const [isRafCode, setIsRafCode] = useState(false)
  const isPetNamePage = pathname.includes(ROUTES.petName.path)
  const isRootPage = pathname.includes(ROUTE_PREFIXES.landingPage)
  const isAddingNewPet =
    isPetNamePage && R.any(R.has("created_at"))(state.context.quote?.pets || [])

  const [isBannerVisible, setIsBannerVisible] = useState(() => {
    try {
      return localStorage?.getItem("bannerClosed") !== "true"
    } catch (error) {
      return false
    }
  })

  const handleCloseBanner = () => {
    setIsBannerVisible(false)
    localStorage.setItem("bannerClosed", "true")
  }

  useEffect(() => {
    const storedPromoCode =
      localStorage.getItem("promoCode") || state.context.quote?.meta?.promo_code
    const storedRafCode = localStorage.getItem("rafCode") || state.context.quote?.meta?.raf_code

    if (storedRafCode) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- sync promo/raf code from localStorage and quote context on mount; setState-in-effect is the intended pattern here
      setPromoCode(storedRafCode)
      setIsRafCode(true)
    } else if (storedPromoCode) {
      setPromoCode(storedPromoCode)
      setIsRafCode(false)
    }
  }, [state.context.quote?.meta?.promo_code, state.context.quote?.meta?.raf_code])

  const isSendMyQuoteVisible =
    SEND_MY_QUOTE_ENABLED &&
    isSendMyQuoteEligible(pathname) &&
    !!state.context.quote?.meta?.quote_id
  // V1 (modal) is the AB-test control, v2 (DLM-1770 pop-in) the tested
  // variant; the backend assignment is read in useSmqVariant.
  const smqVariant = useSmqVariant()

  const completedStepsBarWidth = (getStepNumber(pathname) / 14) * 100
  const isPrepackagesPage = pathname.includes(ROUTES.prepackages.path)
  const isLiabilityPage = pathname.includes(ROUTES.liabilityAddOn.path)
  const isModalitiesPage = pathname.includes(ROUTES.healthCoverageParam.path)
  const isCoveragePage = pathname.includes(ROUTES.healthCoverage.path)
  const showMultiPetPromoBanner =
    (isPrepackagesPage || isLiabilityPage || isModalitiesPage || isCoveragePage) && isMultiPet(state.context.quote)

  return (
    <div className={styles.container}>
      {promoCode && isBannerVisible ? (
        <Banner
          onClose={handleCloseBanner}
          title={
            <Trans
              t={t}
              i18nKey={isRafCode ? "banner.promo-code.raf-title" : "banner.promo-code.title"}
              values={{
                promoCode,
              }}
            />
          }
        />
      ) : isBannerVisible && shouldDisplayBanner() ? (
        <Banner
          onClose={handleCloseBanner}
          title={<Trans t={t} i18nKey={"banner.title"} />}
          subtitle={<Trans t={t} i18nKey={"banner.subtitle"} />}
        />
      ) : null}
      <div className={styles.root}>
        <div className={styles.navbar}>
          {(isPetNamePage && !isAddingNewPet) || isRootPage ? <DalmaLogoLink /> : <BackButton />}
          {isMounted ? (
            <Breadcrumb className={styles.breadcrumb} />
          ) : (
            <Skeleton className={styles.skeleton} />
          )}
          <div className="flex gap-2">
            {isSendMyQuoteVisible ? (
              smqVariant === "v2" ? (
                <SendMyQuoteV2 />
              ) : (
                <SendMyQuote />
              )
            ) : null}
            <SelectLanguage />
          </div>
        </div>
      </div>
      <div className={cn(styles.progressBar, "lg:hidden")}>
        <div
          className={styles.completedStepsBar}
          style={{ width: `${completedStepsBarWidth}%`, transition: "all 0.3s ease" }}
        />
        <div className={styles.activeStep}>
          <div className={styles.activeStepIndicator} />
        </div>
        <div className={styles.uncompletedStepsBar} />
      </div>
      {showMultiPetPromoBanner ? (
        <div className={styles.multiPetPromoBanner} role="banner">
          {t("navbar.multi-pet-promo-banner")}
        </div>
      ) : null}
    </div>
  )
}
